/**
 * Dashboard, „Moje valuace". Klient vidí svoje, tým/admin vše.
 * Prázdný stav = onboarding. Akce: Otevřít (detail), Smazat.
 */
import React, { useEffect, useState } from 'react';
import { listValuations, deleteValuation, getBillingStatus } from '../lib/apiFetch';

function fmtCZK(v) {
  if (v === null || v === undefined) return '-';
  return `${Math.round(v).toLocaleString('cs-CZ')} Kč`;
}
function fmtPct1(v) {
  if (v === null || v === undefined || isNaN(v)) return '-';
  return `${Number(v).toFixed(1)} %`;
}
function fmtDate(s) {
  if (!s) return '-';
  try { return new Date(s).toLocaleDateString('cs-CZ', { day: 'numeric', month: 'numeric', year: 'numeric' }); }
  catch { return s; }
}

const TIER_LABEL = { free: 'FREE', rozsirena: 'ROZŠÍŘENÁ', pro: 'PRO', audit: 'AUDIT', komplet: 'KOMPLET' };
const ACCENT = 'var(--purple-glow)';

// Mini sparkline (vývoj hodnoty v čase) z pole čísel
function Sparkline({ values, width = 160, height = 40 }) {
  const pts = (values || []).filter(v => typeof v === 'number' && !isNaN(v));
  if (pts.length < 2) return null;
  const min = Math.min(...pts), max = Math.max(...pts);
  const span = max - min || 1;
  const step = width / (pts.length - 1);
  const coords = pts.map((v, i) => `${(i * step).toFixed(1)},${(height - ((v - min) / span) * height).toFixed(1)}`);
  const up = pts[pts.length - 1] >= pts[0];
  const color = up ? 'var(--color-success)' : 'var(--color-danger)';
  return (
    <svg width={width} height={height} style={{ display: 'block' }}>
      <polyline points={coords.join(' ')} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx={(width).toFixed(1)} cy={(height - ((pts[pts.length - 1] - min) / span) * height).toFixed(1)} r="3" fill={color} />
    </svg>
  );
}

// Spočítá přehledové statistiky z načtených řádků (per nejnovější firma)
function computeOverview(rows) {
  if (!rows || rows.length === 0) return null;
  const latest = rows[0];
  const company = latest.company_name || '';
  // řádky stejné firmy, chronologicky (staré → nové) pro vývoj
  const sameCompany = rows
    .filter(r => (r.company_name || '') === company)
    .slice()
    .sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
  const totals = sameCompany.map(r => r.result_json?.valuation?.total).filter(v => typeof v === 'number');
  const latestTotal = latest.result_json?.valuation?.total ?? null;
  let trendPct = null;
  if (totals.length >= 2) {
    const prev = totals[totals.length - 2];
    if (prev > 0) trendPct = ((totals[totals.length - 1] - prev) / prev) * 100;
  }
  const met = latest.result_json?.metrics || {};
  return {
    company,
    latestTotal,
    latestDate: latest.created_at,
    trendPct,
    totals,
    analysisCount: rows.length,
    ebitdaMargin: met.ebitda_margin_pct,
    grossMargin: met.gross_margin_pct,
  };
}

function Onboarding({ onNew }) {
  return (
    <div style={onb}>
      <h2 style={{ margin: '0 0 0.4rem', fontFamily: "'Source Serif 4', Georgia, serif", color: 'var(--star-white)', fontSize: '1.5rem' }}>
        Zjistěte hodnotu svého podniku
      </h2>
      <p style={{ color: 'var(--text-dim)', maxWidth: 480, margin: '0 auto 1.5rem', lineHeight: 1.5 }}>
        Z jednoho vyplnění získáte <strong>ocenění firmy</strong> i <strong>audit, kde ztrácíte zisk</strong>.
        Stačí základní čísla, tržby, náklady a pár údajů o byznysu. Na konci si vyberete, co chcete odemknout.
      </p>
      <button onClick={onNew} style={btnPrimary}>Spustit první analýzu</button>
      <div style={{ display: 'flex', gap: '1.5rem', justifyContent: 'center', marginTop: '2rem', flexWrap: 'wrap' }}>
        {[['1', 'Zadáte čísla', 'tržby, náklady, KPI'], ['2', 'Spočítáme', 'ocenění i audit'], ['3', 'Uvidíte výsledek', 'hodnota + kde ztrácíte']].map(([n, t, d]) => (
          <div key={n} style={{ maxWidth: 150 }}>
            <div style={stepNum}>{n}</div>
            <div style={{ color: 'var(--star-white)', fontSize: '0.9rem', fontWeight: 600 }}>{t}</div>
            <div style={{ color: 'var(--text-dim)', fontSize: '0.8rem' }}>{d}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Přehledový panel firmy (jen klient), hodnota, vývoj, sleva, metriky, rychlé akce
function OverviewPanel({ overview, billing, onNew, onNavigate }) {
  const { company, latestTotal, latestDate, trendPct, totals, analysisCount, ebitdaMargin, grossMargin } = overview;
  const up = trendPct !== null && trendPct >= 0;
  const loyalty = billing?.loyalty;
  const pct = billing?.loyalty_percent || 30;
  return (
    <div style={{ marginBottom: '1.75rem' }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '0.9rem' }}>

        {/* Hodnota firmy + vývoj */}
        <div style={{ ...card, gridColumn: 'span 1' }}>
          <div style={cardLabel}>Hodnota firmy{company ? ` · ${company}` : ''}</div>
          <div style={{ fontFamily: "'Source Serif 4', Georgia, serif", color: ACCENT, fontSize: '1.7rem', lineHeight: 1.1 }}>
            {fmtCZK(latestTotal)}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '0.4rem' }}>
            <div>
              {trendPct === null ? (
                <span style={{ fontSize: '0.8rem', color: 'var(--text-dim)' }}>první analýza</span>
              ) : (
                <span style={{ fontSize: '0.85rem', fontWeight: 700, color: up ? 'var(--color-success)' : 'var(--color-danger)' }}>
                  {up ? '▲' : '▼'} {Math.abs(trendPct).toFixed(1)} % oproti minulé
                </span>
              )}
              <div style={{ fontSize: '0.72rem', color: 'var(--text-dim)', marginTop: '0.15rem' }}>k {fmtDate(latestDate)}</div>
            </div>
            <Sparkline values={totals} width={110} height={36} />
          </div>
        </div>

        {/* Věrnostní sleva */}
        <div style={{ ...card, background: loyalty ? 'rgba(47,143,91,0.07)' : 'var(--purple-dark)', borderColor: loyalty ? 'rgba(46,158,91,0.35)' : 'rgba(0,0,0,0.08)' }}>
          <div style={cardLabel}>Věrnostní sleva</div>
          {loyalty ? (
            <>
              <div style={{ fontFamily: "'Source Serif 4', Georgia, serif", color: 'var(--color-success)', fontSize: '1.7rem', lineHeight: 1.1 }}>−{pct} %</div>
              <div style={{ fontSize: '0.82rem', color: 'var(--text-dim)', marginTop: '0.4rem', lineHeight: 1.45 }}>
                Sleva na každé další ocenění i audit. Uplatní se automaticky v platbě.
              </div>
            </>
          ) : (
            <>
              <div style={{ fontFamily: "'Source Serif 4', Georgia, serif", color: 'var(--star-white)', fontSize: '1.25rem', lineHeight: 1.15 }}>Zatím žádná</div>
              <div style={{ fontSize: '0.82rem', color: 'var(--text-dim)', marginTop: '0.4rem', lineHeight: 1.45 }}>
                Po prvním nákupu PRO nebo auditu získáte <strong>{pct} %</strong> slevu na všechny další.
              </div>
            </>
          )}
        </div>

        {/* Klíčové metriky */}
        <div style={card}>
          <div style={cardLabel}>Z poslední analýzy</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.45rem', marginTop: '0.2rem' }}>
            <Metric label="EBITDA marže" value={fmtPct1(ebitdaMargin)} />
            <Metric label="Hrubá marže" value={fmtPct1(grossMargin)} />
            <Metric label="Počet analýz" value={String(analysisCount)} />
          </div>
        </div>
      </div>

      {/* Rychlé akce */}
      <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap', marginTop: '0.9rem' }}>
        <button onClick={onNew} style={btnPrimary}>+ Nová analýza</button>
        <button onClick={() => onNavigate && onNavigate('billing')} style={btnGhost}>Dokoupit PRO / audit</button>
        <button onClick={() => onNavigate && onNavigate('consult')} style={btnGhost}>Konzultace s poradcem</button>
      </div>
    </div>
  );
}

function Metric({ label, value }) {
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: '0.5rem' }}>
      <span style={{ fontSize: '0.82rem', color: 'var(--text-dim)' }}>{label}</span>
      <span style={{ fontSize: '0.95rem', fontWeight: 700, color: 'var(--star-white)' }}>{value}</span>
    </div>
  );
}

export function Dashboard({ isStaff, onOpen, onNew, onNavigate }) {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [billing, setBilling] = useState(null);

  const load = () => {
    setError(null); setRows(null);
    listValuations().then(d => setRows(d.valuations || [])).catch(e => setError(e.message));
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { getBillingStatus().then(setBilling).catch(() => {}); }, []);

  const overview = !isStaff ? computeOverview(rows) : null;

  const remove = async (id, name) => {
    if (!window.confirm(`Smazat valuaci „${name || 'bez názvu'}"? Tuto akci nelze vrátit.`)) return;
    setDeleting(id); setError(null);
    try { await deleteValuation(id); setRows(rs => rs.filter(r => r.id !== id)); }
    catch (e) { setError(e.message); }
    finally { setDeleting(null); }
  };

  return (
    <div style={{ maxWidth: 980, margin: '0 auto', padding: '1.5rem 1rem', fontFamily: "'Inter', sans-serif" }}>
      {/* Přehled firmy (jen klient, když má aspoň jednu analýzu) */}
      {overview && rows && rows.length > 0 && (
        <OverviewPanel overview={overview} billing={billing} onNew={onNew} onNavigate={onNavigate} />
      )}

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.6rem', flexWrap: 'wrap', gap: '0.6rem' }}>
        <h2 style={{ margin: 0, fontFamily: "'Source Serif 4', Georgia, serif", color: 'var(--star-white)', fontSize: '1.4rem' }}>
          {isStaff ? 'Ocenění a audit (všichni klienti)' : 'Ocenění a audit'}
        </h2>
        {(rows && rows.length > 0) && <button onClick={onNew} style={btnPrimary}>+ Nová analýza</button>}
      </div>
      {(rows && rows.length > 0) && (
        <p style={{ color: 'var(--text-dim)', fontSize: '0.88rem', lineHeight: 1.5, margin: '0 0 1.5rem', maxWidth: 640 }}>
          Každá analýza vychází z jednoho vyplnění, dostanete <strong>ocenění firmy</strong> a můžete odemknout i{' '}
          <strong>audit</strong> (kde ztrácíte zisk). Sloupec <em>Varianta</em> ukazuje, co máte odemčené.
        </p>
      )}

      {error && (
        <div style={{ color: 'rgba(192,69,63,0.45)', marginBottom: '1rem' }}>
          Chyba: {error} <button onClick={load} style={linkBtn}>zkusit znovu</button>
        </div>
      )}

      {rows === null && !error && <p style={{ color: 'var(--text-dim)' }}>Načítání…</p>}

      {rows && rows.length === 0 && <Onboarding onNew={onNew} />}

      {rows && rows.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
            <thead>
              <tr style={{ textAlign: 'left', color: 'var(--text-dim)', borderBottom: '1px solid rgba(0,0,0,0.15)' }}>
                <th style={th}>Firma</th><th style={th}>Segment</th><th style={th}>Varianta</th>
                <th style={th}>Hodnota</th><th style={th}>Vytvořeno</th><th style={th}></th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.id} style={{ borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
                  <td style={td}>{r.company_name || '-'}</td>
                  <td style={td}>{r.segment || '-'}</td>
                  <td style={td}>{TIER_LABEL[r.tier] || r.tier}</td>
                  <td style={td}>{fmtCZK(r.result_json?.valuation?.total)}</td>
                  <td style={td}>{fmtDate(r.created_at)}</td>
                  <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                    <button onClick={() => onOpen(r)} style={linkBtn}>Otevřít</button>
                    <button onClick={() => remove(r.id, r.company_name)} disabled={deleting === r.id}
                            style={{ ...linkBtn, color: 'rgba(192,69,63,0.45)', marginLeft: '0.9rem' }}>
                      {deleting === r.id ? '…' : 'Smazat'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const th = { padding: '0.6rem 0.75rem', fontWeight: 600, fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.05em' };
const td = { padding: '0.7rem 0.75rem', color: 'var(--star-white)' };
const btnPrimary = {
  padding: '0.6rem 1.2rem', borderRadius: 8, border: 'none',
  background: 'var(--purple-glow)', color: '#FFFFFF',
  fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer', letterSpacing: '0.03em',
};
const btnGhost = {
  padding: '0.6rem 1.1rem', borderRadius: 8, background: 'transparent',
  border: '1px solid rgba(91,59,140,0.4)', color: 'var(--purple-glow)',
  fontWeight: 600, fontSize: '0.85rem', cursor: 'pointer',
};
const card = {
  background: 'var(--purple-dark)', border: '1px solid rgba(0,0,0,0.08)', borderRadius: 14,
  padding: '1.1rem 1.2rem', boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
  display: 'flex', flexDirection: 'column',
};
const cardLabel = {
  fontSize: '0.72rem', color: 'var(--text-dim)', textTransform: 'uppercase',
  letterSpacing: '0.06em', fontWeight: 700, marginBottom: '0.4rem',
};
const linkBtn = {
  background: 'transparent', border: 'none', color: 'var(--gold)', cursor: 'pointer',
  fontSize: '0.85rem', padding: 0, fontFamily: "'Inter', sans-serif",
};
const onb = {
  textAlign: 'center', padding: '3rem 1.5rem',
  border: '1px solid rgba(0,0,0,0.15)', borderRadius: 16,
  background: 'radial-gradient(ellipse 70% 60% at 50% 0%, rgba(107,63,160,0.18) 0%, transparent 70%)',
};
const stepNum = {
  width: 30, height: 30, borderRadius: '50%', margin: '0 auto 0.4rem',
  background: 'var(--purple-glow)', color: '#FFFFFF',
  display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: '0.9rem',
};
